<template>
	<div class="overview-wrapper">
		<div class="overview-header">
			<div class="profile-title">
				<ProfilePic :user="org" size="100px" />
				<div class="org-title">{{ org.name }}</div>
			</div>
			<Button primary @click="showEditModal = true">
				{{ $t('common_button_edit') }}
			</Button>
		</div>
		<div class="org-details">
			<i18n-t
				v-if="org.created_at"
				keypath="admin_overview_created_on"
				tag="div"
			>
				<template #Date>
					<span v-text="dateFormat(org.created_at, 'L')" />
				</template>
			</i18n-t>
			<i18n-t v-if="org.id" keypath="admin_overview_org_id" tag="div">
				<template #OrgId><span v-text="org.id" /></template>
			</i18n-t>
		</div>
		<div class="options-container">
			<RouterLink v-for="item in navItems" :key="item.title" :to="item.to">
				<div class="nav-item pointer" aria-label="">
					<div class="icon-title">
						<component :is="item.icon" />
						<div v-if="item.showCount" class="title-count-wrapper">
							<div class="title" v-text="$t(item.title)" />
							<div class="count" v-text="formatNumber(item.count)" />
						</div>
						<div v-if="!item.showCount" class="title" v-text="$t(item.title)" />
					</div>
					<ChevronIcon class="chevron-icon" right />
				</div>
			</RouterLink>
		</div>
		<EditOrg
			v-if="showEditModal"
			:org="org"
			@cancel="showEditModal = false"
			@close="showEditModal = false"
			@success="showEditModal = false"
		/>
	</div>
</template>

<script>
import { markRaw } from 'vue'
import store, { getOrganization } from '../../store'
import EditOrg from './EditOrg'
import { Button, ProfilePic } from '@/components/common'
import { dateFormat } from '@/helperMethods'
import { get } from './common'
import {
	ChevronIcon,
	UserIcon,
	GroupsIcon,
	CategoriesIcon,
	ChartIcon,
	LockIcon,
	FileArrowUpIcon,
	GearIcon,
	IntegrationsIcon,
} from '@/icons'
import { formatNumber } from '@/i18n'

// const NavItem = {
// 	name: 'NavItem',
// 	functional: true,
// 	render(h, { props, parent }) {
// 		const Icon = props.icon // transform into capital case so we can render icon
// 		const count = Number.isInteger(props.count)
// 			? formatNumber(props.count)
// 			: props.count
// 		return (
// 		)
// 	},
// }

export default {
	name: 'Overview',
	components: {
		ProfilePic,
		Button,
		ChevronIcon,
		UserIcon,
		GroupsIcon,
		CategoriesIcon,
		ChartIcon,
		LockIcon,
		EditOrg,
		// NavItem,
		FileArrowUpIcon,
		GearIcon,
		IntegrationsIcon,
	},

	data() {
		return {
			store,
			userCount: '--',
			groupCount: '--',
			categoryCount: '--',
			showEditModal: false,
			org: getOrganization() || {},
		}
	},

	mounted() {
		this.$onEventBus('user-update', () => {
			this.org = getOrganization()
		})

		this.fetchOrgStats()
	},

	computed: {
		navItems() {
			let items = [
				{
					title: 'admin_overview_people_title',
					showCount: true,
					count: this.userCount,
					icon: UserIcon,
					to: { name: 'users' },
				},
				{
					title: 'admin_groups_title',
					showCount: true,
					count: this.groupCount,
					icon: GroupsIcon,
					to: { name: 'groups' },
				},
				{
					title: 'admin_overview_categories_title',
					showCount: true,
					count: this.categoryCount,
					icon: CategoriesIcon,
					to: { name: 'categories' },
				},
				{
					title: 'admin_overview_reports_title',
					icon: ChartIcon,
					to: { name: 'reports' },
				},
				{
					title: 'admin_overview_roles_permissions_title',
					icon: LockIcon,
					to: { name: 'permissions' },
				},
				{
					title: 'settings_title',
					icon: GearIcon,
					to: { name: 'settings' },
				},
			]

			if (this.org.imports_enabled) {
				items.push({
					title: 'admin_sidebar_imports_label',
					icon: FileArrowUpIcon,
					to: { name: 'imports' },
				})
			}

			if (this.org.integrations_enabled) {
				items.push({
					title: 'admin_overview_integrations_title',
					icon: IntegrationsIcon,
					to: { name: 'integrations' },
				})
			}

			return items
		},
	},

	methods: {
		dateFormat,
		formatNumber(num) {
			return Number.isInteger(num) ? formatNumber(num) : num
		},
		async fetchOrgStats() {
			try {
				const { data } = await get('/organization')
				Object.assign(this, {
					userCount: data.users_count || '--',
					groupCount: data.chats_count || '--',
					categoryCount: data.categories_count || '--',
				})
			} catch (e) {
				console.error(e)
				this.$alert.error()
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.overview-wrapper {
	display: flex;
	flex-flow: row wrap;
	height: 100vh;
	background: #fafafa;
	overflow-y: auto;
}

.overview-header {
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 165px;
	padding: 0 32px;
	align-items: center;
	justify-content: space-between;
	border-bottom: 1px solid var(--neutral-200);
}

.profile-title {
	display: flex;
	align-items: center;
}

.org-title {
	font-family: Avenir-Heavy;
	font-size: 36px;
	font-weight: 900px;
	color: var(--neutral-800);
	margin-left: 32px;
}

.org-details {
	margin-left: 158px;
	margin-top: -60px;
	font-family: Avenir-Roman;
	font-size: 14px;
	color: #858585;
	& span {
		font-family: Avenir-Heavy;
		font-size: 14px;
		color: #6a6c70;
	}
}

.options-container {
	$horizontal-gap: 60px;
	$half-gap: 30px;
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	padding: 32px;
	gap: 42px $horizontal-gap;
	height: 100%;
	align-content: flex-start;

	a {
		text-decoration: none;
		color: inherit;
		width: calc(50% - #{$half-gap});

		@media screen and (max-width: 1112px) {
			width: 100%;
		}
	}
}

.nav-item {
	display: flex;
	border: 1px solid #e2e4e6;
	border-radius: 6px;
	height: 100px;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 0 32px 0 32px;
	background: white;
	& .icon-title {
		display: flex;
		align-items: center;
	}
	&:hover {
		border-color: var(--blue-300);
		.chevron-icon {
			color: var(--blue-300);
		}
	}

	.title,
	.count {
		font-family: Avenir-Heavy;
		font-size: 20px;
		color: #4f5053;
	}

	.title-count-wrapper {
		& .title {
			font-family: Avenir-Book;
			font-size: 14px;
			color: #4f5053;
			text-transform: uppercase;
		}
	}

	.pronto-icon {
		font-size: 24px;
		margin-right: 24px;
		:deep(svg) {
			height: 24px;
			width: 24px;
		}
	}
}
</style>
